'use client';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useSelect } from 'downshift';
import { useState, useRef, useMemo, useEffect, memo, useLayoutEffect, createContext, useContext } from 'react';
import { c as clsx, S as Slot } from './Accordion-client-BOvH9Oo6.mjs';

const SelectContext = /*#__PURE__*/ createContext(undefined);
const itemToString = (item)=>item?.value ?? "";
const SelectRoot = ({ children, defaultValue, name, onValueChange, value, ref, className, required, ...props })=>{
    const [items, setItems] = useState(new Set());
    const hasReset = useRef(false);
    const itemArray = useMemo(()=>{
        return Array.from(items);
    }, [
        items
    ]);
    const downshift = useSelect({
        items: itemArray,
        itemToString,
        onSelectedItemChange: ({ selectedItem })=>{
            if (onValueChange) {
                onValueChange(selectedItem.value);
            }
        },
        selectedItem: itemArray.find((item)=>item.value === value),
        defaultSelectedItem: itemArray.find((item)=>item.value === defaultValue)
    });
    useEffect(()=>{
        if (items.size && !hasReset.current) {
            downshift.reset();
            hasReset.current = true;
        }
    }, [
        items
    ]);
    const contextValue = useMemo(()=>({
            items,
            setItems,
            ...downshift
        }), [
        items,
        setItems,
        downshift
    ]);
    return /*#__PURE__*/ jsxs(SelectContext.Provider, {
        value: contextValue,
        children: [
            /*#__PURE__*/ jsx("div", {
                className: clsx("fr-select-root", className),
                ...props,
                children: children
            }),
            name && /*#__PURE__*/ jsx("input", {
                required: required,
                type: "hidden",
                name: name,
                ref: ref,
                value: downshift.selectedItem?.value ?? ""
            })
        ]
    });
};
const useSelectContext = ()=>{
    const context = useContext(SelectContext);
    if (context === undefined) {
        throw new Error("useSelectContext must be used within a SelectRoot");
    }
    return context;
};
const SelectContent = /*#__PURE__*/ memo(({ children, asChild, className, ...rest })=>{
    const { getMenuProps } = useSelectContext();
    const Component = asChild ? Slot : "div";
    return /*#__PURE__*/ jsx(Component, {
        className: className,
        ...getMenuProps(),
        ...rest,
        children: children
    });
});
SelectContent.displayName = "SelectContent";
const SelectIndicator = ({ asChild, className, ...props })=>{
    const { isOpen } = useSelectContext();
    if (asChild) {
        return /*#__PURE__*/ jsx(Slot, {
            className: className,
            "data-state": isOpen ? "open" : "closed",
            ...props
        });
    }
    return /*#__PURE__*/ jsx("svg", {
        className: className,
        style: {
            transition: "transform 0.3s ease",
            transform: isOpen ? `rotate(180deg)` : undefined
        },
        xmlns: "http://www.w3.org/2000/svg",
        width: "15",
        height: "15",
        fill: "none",
        viewBox: "0 0 15 15",
        ...props,
        children: /*#__PURE__*/ jsx("path", {
            stroke: "var(--svg-color, currentColor)",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M4.5 6l3 3 3-3"
        })
    });
};
SelectIndicator.displayName = "SelectIndicator";
const SelectItem = ({ children, value, asChild, className, ...rest })=>{
    const { items, selectedItem, highlightedIndex, getItemProps, setItems } = useSelectContext();
    const isSelected = selectedItem?.value === value;
    const itemValue = useMemo(()=>{
        return {
            value: value,
            jsxValue: children
        };
    }, [
        value,
        children
    ]);
    useLayoutEffect(()=>{
        if (value) {
            // Add
            setItems((prev)=>{
                const newSet = new Set(prev);
                if (!newSet.has(itemValue)) {
                    newSet.add(itemValue);
                }
                return newSet;
            });
        }
        return ()=>{
            // Remove
            setItems((prev)=>{
                const newSet = new Set(prev);
                if (newSet.has(itemValue)) {
                    newSet.delete(itemValue);
                }
                return newSet;
            });
        };
    }, [
        itemValue
    ]);
    const Component = asChild ? Slot : "div";
    const foundItem = useMemo(()=>{
        const itemArr = Array.from(items);
        const itemIndex = itemArr.findIndex((item)=>item.value === value);
        if (itemIndex === -1) return;
        const item = itemArr[itemIndex];
        if (!item) return;
        return {
            item,
            index: itemIndex
        };
    }, [
        items,
        value
    ]);
    if (!foundItem) return null;
    return /*#__PURE__*/ jsx(Component, {
        "data-selected": isSelected ? true : undefined,
        "data-highlighted": highlightedIndex === foundItem.index ? true : undefined,
        ...getItemProps({
            item: foundItem.item,
            index: foundItem.index
        }),
        className: className,
        ...rest,
        children: children
    });
};
SelectItem.displayName = "SelectItem";
const SelectLabel = ({ children, className, asChild, ...props })=>{
    const Component = asChild ? Slot : "label";
    return /*#__PURE__*/ jsx(Component, {
        className: className,
        ...props,
        children: children
    });
};
SelectLabel.displayName = "SelectLabel";
const SelectReset = ({ children, className, asChild, ...props })=>{
    const { reset } = useSelectContext();
    const Component = asChild ? Slot : "button";
    return /*#__PURE__*/ jsx(Component, {
        type: "button",
        className: className,
        onClick: reset,
        ...props,
        children: children || /*#__PURE__*/ jsx("span", {
            children: "Reset field"
        })
    });
};
SelectReset.displayName = "SelectReset";
const SelectTrigger = ({ children, className, asChild, ...props })=>{
    const { getToggleButtonProps } = useSelectContext();
    const Component = asChild ? Slot : "button";
    return /*#__PURE__*/ jsx(Component, {
        className: className,
        type: "button",
        ...getToggleButtonProps(props),
        children: children || /*#__PURE__*/ jsx(SelectValue, {})
    });
};
SelectTrigger.displayName = "SelectTrigger";
const SelectValue = ({ placeholder = "Select a value" })=>{
    const { selectedItem } = useSelectContext();
    const displayValue = selectedItem?.jsxValue ?? placeholder;
    return /*#__PURE__*/ jsx(Fragment, {
        children: displayValue
    });
};
SelectValue.displayName = "SelectValue";
const SelectViewport = ({ children, className, ...props })=>{
    const { isOpen } = useSelectContext();
    return /*#__PURE__*/ jsx("dialog", {
        className: clsx(className, "fr-select-viewport"),
        ...props,
        open: isOpen,
        "data-state": isOpen ? "open" : "closed",
        children: children
    });
};
SelectViewport.displayName = "SelectViewport";

var Select = {
  __proto__: null,
  SelectContent: SelectContent,
  SelectIndicator: SelectIndicator,
  SelectItem: SelectItem,
  SelectLabel: SelectLabel,
  SelectReset: SelectReset,
  SelectRoot: SelectRoot,
  SelectTrigger: SelectTrigger,
  SelectValue: SelectValue,
  SelectViewport: SelectViewport
};

export { Select as S, SelectRoot as a, SelectContent as b, SelectIndicator as c, SelectItem as d, SelectLabel as e, SelectReset as f, SelectTrigger as g, SelectValue as h, SelectViewport as i };
